import * as genericService from "./genericFormServices";
import type { PLOFormFiveData } from "../types/PLOs/plo_five.type";
import type { SubmissionStatus } from "../types/MainType.type";

const ENDPOINT = "plo-form-five";

export interface PLOFetchResponse {
  data: PLOFormFiveData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for PLO Form Five
// ---------------------------------------------------------------------------
export const mapPloFormFiveBackendToFrontend = (item: any): PLOFormFiveData => {
  if (!item) return {} as PLOFormFiveData;

  // Extract nested form entity from backend response structure (PLO Form Five)
  const form = item.pLOFormFive || item.ploFormFive || item.formData || item;

  return {
    id: item.id || form.id || '',
    documentNumber: item.documentNumber || form.documentNumber || '',
    formType: item.formType || form.formType || '',
    
    // Core Form Five Fields
    officeId: form.officeId || item.officeId || '',

    gender: form.gender || '',
    victimAge: form.victimAge || '',
    victimOrWitnessName: form.victimOrWitnessName || '',
    interviewDate: form.interviewDate || '',
    ptiFirstDate: form.ptiFirstDate || '',
    ptiSecondDate: form.ptiSecondDate || '',
    interviewerName: form.interviewerName || '',
    interviewerRole: form.interviewerRole || '',
    judgmentType: form.judgmentType || '',
    judgmentDate: form.judgmentDate || '',
    courtName: form.courtName || '',
    appealOrRevision: form.appealOrRevision || '',
    submissionDate: form.submissionDate || '',
    remarks: form.remarks || '',

    // Status & Metadata
    status: (item.status || form.status || 'DRAFT') as SubmissionStatus,
    submitDate: item.submitDate || form.submitDate || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // System Dates & Audit
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory) ? item.stageHistory : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

export interface PloFiveFetchParams {
  searchTerm?: string;
  courtSearch?: string;
  partySearch?: string;
  selectedStatus?: string;
  ptiFirstDate?: string;
  ptiSecondDate?: string;
  currentPage: number;
  itemsPerPage: number;
}

// ---------------------------------------------------------------------------
// 2. Filter Function for PLO Form Five
// ---------------------------------------------------------------------------
const ploFilterPredicate = (
  app: PLOFormFiveData,
  params: PloFiveFetchParams
): boolean => {
  const { searchTerm, courtSearch, partySearch, selectedStatus, ptiFirstDate, ptiSecondDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  
  // PTI Date matches
  const matchesPtiFirstDate =
    !ptiFirstDate ||
    Boolean(app.ptiFirstDate && app.ptiFirstDate.startsWith(ptiFirstDate));

  const matchesPtiSecondDate =
    !ptiSecondDate ||
    Boolean(app.ptiSecondDate && app.ptiSecondDate.startsWith(ptiSecondDate));

  // Court search match
  const courtTerm = (courtSearch || "").toLowerCase().trim();
  const matchesCourt =
    !courtTerm ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(courtTerm));

  // Party search match (Victim / Witness)
  const partyTerm = (partySearch || "").toLowerCase().trim();
  const matchesParty =
    !partyTerm ||
    Boolean(app.victimOrWitnessName && app.victimOrWitnessName.toLowerCase().includes(partyTerm));

  // General search term across key text fields
  const term = (searchTerm || "").toLowerCase().trim();
  const matchesGeneralSearch =
    !term ||
    Boolean(app.victimOrWitnessName && app.victimOrWitnessName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerName && app.interviewerName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerRole && app.interviewerRole.toLowerCase().includes(term)) ||
    Boolean(app.remarks && app.remarks.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(
    matchesStatus &&
      matchesPtiFirstDate &&
      matchesPtiSecondDate &&
      matchesCourt &&
      matchesParty &&
      matchesGeneralSearch
  );
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

// Fetch All / Filtered Applications
export const fetchPloApplications = (
  params: PloFiveFetchParams
): Promise<PLOFetchResponse> =>
  genericService.fetchApplications<PLOFormFiveData>(
    ENDPOINT,
    params,
    mapPloFormFiveBackendToFrontend,
    ploFilterPredicate
  );

// Fetch Single Application by ID
export const fetchPloById = (id: string): Promise<PLOFormFiveData> =>
  genericService.fetchApplicationById<PLOFormFiveData>(
    ENDPOINT,
    id,
    mapPloFormFiveBackendToFrontend
  );

// Save Draft (Single item or Array/Bulk payload)
export const savePloDraft = (
  id: string | null,
  data: Partial<PLOFormFiveData> | Partial<PLOFormFiveData>[]
) => genericService.saveDraft<PLOFormFiveData>(ENDPOINT, id, data as any);

// Delete Form (Soft Delete)
export const deletePloForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);